import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { platformSaasBillingCatalogService } from '../../services/api';
import { usePlatformActionPermissions } from '../../hooks/usePlatformActionPermissions';

const { Title, Text } = Typography;

const PLAN_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Archived', value: 'archived' },
];

const PLAN_STATUS_PATCH_OPTIONS = [
  { label: 'Inactive', value: 'inactive' },
  { label: 'Archived', value: 'archived' },
];

const FEATURE_TYPE_OPTIONS = [
  { label: 'Boolean', value: 'boolean' },
  { label: 'Limit', value: 'limit' },
];

const FEATURE_STATUS_PATCH_OPTIONS = [
  { label: 'Inactive', value: 'inactive' },
  { label: 'Archived', value: 'archived' },
];

const statusColorMap = {
  active: 'green',
  inactive: 'gold',
  archived: 'default',
};

const formatCentsToBrl = (value) => {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format((Number.isFinite(numeric) ? numeric : 0) / 100);
};

const parseBrlToCents = (rawValue) => {
  if (rawValue === null || rawValue === undefined) {
    return null;
  }

  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return Math.round(rawValue * 100);
  }

  const text = String(rawValue).trim().replace(/\s/g, '').replace(/R\$/gi, '');
  if (!text) {
    return null;
  }

  let normalized = text;
  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    normalized = normalized.replace(',', '.');
  }

  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return null;
  }

  return Math.round(numeric * 100);
};

const BILLING_PERIOD_OPTIONS = [
  { label: 'Mensal (1 mes)', value: 1 },
  { label: 'Trimestral (3 meses)', value: 3 },
  { label: 'Semestral (6 meses)', value: 6 },
  { label: 'Anual (12 meses)', value: 12 },
];

const extractApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;

  if (!detail) {
    return fallback;
  }

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .filter(Boolean)
      .join(' | ');
  }

  if (typeof detail === 'object') {
    return detail.message || JSON.stringify(detail);
  }

  return fallback;
};

const PERMISSION_TOOLTIP_MESSAGE = 'Você não possui permissão para executar esta ação';

const withPermissionTooltip = (node, disabledByPermission) => {
  if (!disabledByPermission) {
    return node;
  }

  return (
    <Tooltip title={PERMISSION_TOOLTIP_MESSAGE}>
      <span style={{ display: 'inline-block' }}>{node}</span>
    </Tooltip>
  );
};

const PlatformSaasBillingCatalog = () => {
  const { canExecuteAction } = usePlatformActionPermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [entitlements, setEntitlements] = useState([]);

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);

  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [createFeatureOpen, setCreateFeatureOpen] = useState(false);
  const [createEntitlementOpen, setCreateEntitlementOpen] = useState(false);

  const [statusPlanModalState, setStatusPlanModalState] = useState({ open: false, plan: null });
  const [statusFeatureModalState, setStatusFeatureModalState] = useState({ open: false, feature: null });

  const [submitting, setSubmitting] = useState(false);

  const [planForm] = Form.useForm();
  const [featureForm] = Form.useForm();
  const [entitlementForm] = Form.useForm();
  const [planStatusForm] = Form.useForm();
  const [featureStatusForm] = Form.useForm();

  const [createEntitlementFeatureType, setCreateEntitlementFeatureType] = useState(null);

  const canCreatePlan = canExecuteAction('catalog.plans.create');
  const canCreateFeature = canExecuteAction('catalog.features.write');
  const canCreateEntitlement = canExecuteAction('catalog.entitlements.create');
  const canWritePlanStatus = canExecuteAction('catalog.plans.status.write');
  const canWritePlanIsPublic = canExecuteAction('catalog.plans.is_public.write');
  const canWriteFeatureStatus = canExecuteAction('catalog.features.status.write');
  const canDeleteEntitlement = canExecuteAction('catalog.entitlements.delete');

  const featuresById = useMemo(() => {
    const map = new Map();
    (features || []).forEach((feature) => map.set(feature.id, feature));
    return map;
  }, [features]);

  const selectedPlan = useMemo(() => {
    return (plans || []).find((plan) => plan.id === selectedPlanId) || null;
  }, [plans, selectedPlanId]);

  const loadBaseCatalog = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [plansResponse, featuresResponse] = await Promise.all([
        platformSaasBillingCatalogService.listPlans(),
        platformSaasBillingCatalogService.listFeatures(),
      ]);

      const loadedPlans = Array.isArray(plansResponse?.data) ? plansResponse.data : [];
      const loadedFeatures = Array.isArray(featuresResponse?.data) ? featuresResponse.data : [];

      setPlans(loadedPlans);
      setFeatures(loadedFeatures);

      if (!selectedPlanId && loadedPlans.length > 0) {
        setSelectedPlanId(loadedPlans[0].id);
      }

      if (selectedPlanId && !loadedPlans.some((plan) => plan.id === selectedPlanId)) {
        setSelectedPlanId(loadedPlans.length > 0 ? loadedPlans[0].id : null);
      }
    } catch (requestError) {
      console.error(requestError);
      setError(extractApiErrorMessage(requestError, 'Nao foi possivel carregar o catalogo SaaS.'));
    } finally {
      setLoading(false);
    }
  }, [selectedPlanId]);

  const loadPlanEntitlements = useCallback(async (planId) => {
    if (!planId) {
      setEntitlements([]);
      return;
    }

    setEntitlementsLoading(true);

    try {
      const response = await platformSaasBillingCatalogService.listPlanEntitlements(planId);
      setEntitlements(Array.isArray(response?.data) ? response.data : []);
    } catch (requestError) {
      console.error(requestError);
      message.error(extractApiErrorMessage(requestError, 'Nao foi possivel carregar os entitlements do plano.'));
      setEntitlements([]);
    } finally {
      setEntitlementsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBaseCatalog();
  }, [loadBaseCatalog]);

  useEffect(() => {
    loadPlanEntitlements(selectedPlanId);
  }, [loadPlanEntitlements, selectedPlanId]);

  const refreshAll = async () => {
    await loadBaseCatalog();
    await loadPlanEntitlements(selectedPlanId);
  };

  const handleCreatePlan = async () => {
    try {
      const values = await planForm.validateFields();
      const basePriceCents = parseBrlToCents(values.base_price_brl);

      if (basePriceCents === null) {
        message.error('Informe um preco valido em R$.');
        return;
      }

      const payload = {
        code: values.code,
        name: values.name,
        status: values.status,
        is_public: values.is_public ?? false,
        billing_period_months: values.billing_period_months,
        base_price_cents: basePriceCents,
        currency_code: values.currency_code,
        trial_days_default: values.trial_days_default,
      };

      setSubmitting(true);
      await platformSaasBillingCatalogService.createPlan(payload);
      message.success('Plano criado com sucesso.');
      setCreatePlanOpen(false);
      planForm.resetFields();
      await refreshAll();
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      message.error(extractApiErrorMessage(requestError, 'Nao foi possivel criar o plano.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateFeature = async () => {
    try {
      const values = await featureForm.validateFields();
      const payload = {
        code: values.code,
        name: values.name,
        feature_type: values.feature_type,
        status: values.status,
      };

      setSubmitting(true);
      await platformSaasBillingCatalogService.createFeature(payload);
      message.success('Feature criada com sucesso.');
      setCreateFeatureOpen(false);
      featureForm.resetFields();
      await refreshAll();
    } catch (requestError) {
      if (requestError?.errorFields) {
        message.warning('Preencha os campos obrigatorios da feature.');
        return;
      }
      message.error(extractApiErrorMessage(requestError, 'Nao foi possivel criar a feature.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePatchPlanStatus = async () => {
    if (!statusPlanModalState.plan?.id) {
      return;
    }

    try {
      const values = await planStatusForm.validateFields();
      setSubmitting(true);
      await platformSaasBillingCatalogService.patchPlanStatus(statusPlanModalState.plan.id, values);
      message.success('Status do plano atualizado.');
      setStatusPlanModalState({ open: false, plan: null });
      planStatusForm.resetFields();
      await refreshAll();
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      message.error(extractApiErrorMessage(requestError, 'Nao foi possivel atualizar o status do plano.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePatchPlanIsPublic = (record) => {
    const newValue = !record.is_public;
    Modal.confirm({
      title: newValue ? 'Tornar plano público' : 'Tornar plano privado',
      content: newValue
        ? `O plano "${record.code}" passará a aparecer no signup público.`
        : `O plano "${record.code}" deixará de aparecer no signup público.`,
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await platformSaasBillingCatalogService.patchPlanIsPublic(record.id, { is_public: newValue });
          message.success(newValue ? 'Plano marcado como público.' : 'Plano marcado como privado.');
          await refreshAll();
        } catch (requestError) {
          message.error(extractApiErrorMessage(requestError, 'Nao foi possivel alterar visibilidade do plano.'));
        }
      },
    });
  };

  const handlePatchFeatureStatus = async () => {
    if (!statusFeatureModalState.feature?.id) {
      return;
    }

    try {
      const values = await featureStatusForm.validateFields();
      setSubmitting(true);
      await platformSaasBillingCatalogService.patchFeatureStatus(statusFeatureModalState.feature.id, values);
      message.success('Status da feature atualizado.');
      setStatusFeatureModalState({ open: false, feature: null });
      featureStatusForm.resetFields();
      await refreshAll();
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      message.error(extractApiErrorMessage(requestError, 'Nao foi possivel atualizar o status da feature.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateEntitlement = async () => {
    if (!selectedPlanId) {
      message.warning('Selecione um plano antes de vincular entitlement.');
      return;
    }

    try {
      const values = await entitlementForm.validateFields();
      const payload = {
        feature_id: values.feature_id,
        bool_value: values.bool_value,
        limit_value: values.limit_value,
      };

      if (createEntitlementFeatureType === 'boolean') {
        payload.limit_value = null;
      }
      if (createEntitlementFeatureType === 'limit') {
        payload.bool_value = null;
      }

      setSubmitting(true);
      await platformSaasBillingCatalogService.createPlanEntitlement(selectedPlanId, payload);
      message.success('Entitlement vinculado com sucesso.');
      setCreateEntitlementOpen(false);
      entitlementForm.resetFields();
      setCreateEntitlementFeatureType(null);
      await loadPlanEntitlements(selectedPlanId);
    } catch (requestError) {
      if (requestError?.errorFields) {
        return;
      }
      message.error(extractApiErrorMessage(requestError, 'Nao foi possivel vincular entitlement.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntitlement = async (record) => {
    if (!selectedPlanId) {
      return;
    }

    try {
      setSubmitting(true);
      await platformSaasBillingCatalogService.deletePlanEntitlement(selectedPlanId, record.id);
      message.success('Entitlement excluido com sucesso.');
      await loadPlanEntitlements(selectedPlanId);
    } catch (requestError) {
      message.error(extractApiErrorMessage(requestError, 'Nao foi possivel excluir entitlement.'));
    } finally {
      setSubmitting(false);
    }
  };

  const planColumns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => <Tag color={statusColorMap[value] || 'default'}>{String(value || '-').toUpperCase()}</Tag>,
    },
    {
      title: 'Periodo (meses)',
      dataIndex: 'billing_period_months',
      key: 'billing_period_months',
    },
    {
      title: 'Preco base',
      dataIndex: 'base_price_cents',
      key: 'base_price_cents',
      render: (value) => formatCentsToBrl(value),
    },
    {
      title: 'Trial (dias)',
      dataIndex: 'trial_days_default',
      key: 'trial_days_default',
    },
    {
      title: 'Publico',
      dataIndex: 'is_public',
      key: 'is_public',
      render: (value) => (
        <Tag color={value ? 'blue' : 'default'}>{value ? 'SIM' : 'NAO'}</Tag>
      ),
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {withPermissionTooltip(
            <Button
              size="small"
              disabled={!canWritePlanStatus}
              onClick={() => {
                planStatusForm.setFieldsValue({ status: record.status === 'active' ? 'inactive' : 'archived' });
                setStatusPlanModalState({ open: true, plan: record });
              }}
            >
              Alterar status
            </Button>,
            !canWritePlanStatus
          )}
          {withPermissionTooltip(
            <Button
              size="small"
              disabled={!canWritePlanIsPublic}
              onClick={() => handlePatchPlanIsPublic(record)}
            >
              {record.is_public ? 'Tornar Privado' : 'Tornar Publico'}
            </Button>,
            !canWritePlanIsPublic
          )}
        </Space>
      ),
    },
  ];

  const featureColumns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Tipo',
      dataIndex: 'feature_type',
      key: 'feature_type',
      render: (value) => String(value || '-').toUpperCase(),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => <Tag color={statusColorMap[value] || 'default'}>{String(value || '-').toUpperCase()}</Tag>,
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, record) => (
        withPermissionTooltip(
          <Button
            size="small"
            disabled={!canWriteFeatureStatus}
            onClick={() => {
              featureStatusForm.setFieldsValue({ status: record.status === 'active' ? 'inactive' : 'archived' });
              setStatusFeatureModalState({ open: true, feature: record });
            }}
          >
            Alterar status
          </Button>,
          !canWriteFeatureStatus
        )
      ),
    },
  ];

  const entitlementColumns = [
    {
      title: 'Feature',
      key: 'feature',
      render: (_, record) => {
        const feature = featuresById.get(record.feature_id);
        if (!feature) {
          return <Text type="secondary">Feature nao encontrada ({record.feature_id})</Text>;
        }

        return (
          <Space direction="vertical" size={0}>
            <Text>{feature.name}</Text>
            <Text type="secondary">{feature.code}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Tipo',
      key: 'feature_type',
      render: (_, record) => {
        const feature = featuresById.get(record.feature_id);
        return String(feature?.feature_type || '-').toUpperCase();
      },
    },
    {
      title: 'Valor',
      key: 'value',
      render: (_, record) => {
        const feature = featuresById.get(record.feature_id);
        const type = feature?.feature_type;

        if (type === 'boolean') {
          return record.bool_value ? 'TRUE' : 'FALSE';
        }

        if (type === 'limit') {
          return Number(record.limit_value || 0);
        }

        return '-';
      },
    },
    {
      title: 'Status da feature',
      key: 'feature_status',
      render: (_, record) => {
        const feature = featuresById.get(record.feature_id);
        return <Tag color={statusColorMap[feature?.status] || 'default'}>{String(feature?.status || '-').toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, record) => (
        withPermissionTooltip(
          <Button
            size="small"
            danger
            loading={submitting}
            disabled={!canDeleteEntitlement}
            onClick={() => {
              Modal.confirm({
                title: 'Excluir entitlement',
                content: 'Tem certeza? Esta acao so sera permitida se ainda for seguro excluir.',
                okText: 'Excluir',
                cancelText: 'Cancelar',
                okButtonProps: { danger: true },
                onOk: async () => {
                  await handleDeleteEntitlement(record);
                },
              });
            }}
          >
            Excluir
          </Button>,
          !canDeleteEntitlement
        )
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="Carregando catalogo SaaS..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>Catalogo SaaS</Title>
      <Text type="secondary">Gestao de planos, features e entitlements do catalogo comercial SaaS.</Text>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Erro ao carregar"
          description={error}
          style={{ marginTop: 16, marginBottom: 16 }}
        />
      ) : null}

      <Tabs
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'plans',
            label: 'Planos',
            children: (
              <Card
                extra={
                  withPermissionTooltip(
                    <Button
                      type="primary"
                      disabled={!canCreatePlan}
                      onClick={() => {
                        planForm.resetFields();
                        planForm.setFieldsValue({
                          status: 'active',
                          is_public: false,
                          billing_period_months: 1,
                          base_price_brl: '0,00',
                          currency_code: 'BRL',
                          trial_days_default: 0,
                        });
                        setCreatePlanOpen(true);
                      }}
                    >
                      Novo plano
                    </Button>,
                    !canCreatePlan
                  )
                }
              >
                {plans.length === 0 ? (
                  <Empty description="Nenhum plano cadastrado ainda." />
                ) : (
                  <Table
                    rowKey="id"
                    dataSource={plans}
                    columns={planColumns}
                    pagination={{ pageSize: 10 }}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'features',
            label: 'Features',
            children: (
              <Card
                extra={
                  withPermissionTooltip(
                    <Button
                      type="primary"
                      disabled={!canCreateFeature}
                      onClick={() => {
                        featureForm.resetFields();
                        featureForm.setFieldsValue({
                          feature_type: 'boolean',
                          status: 'active',
                        });
                        setCreateFeatureOpen(true);
                      }}
                    >
                      Nova feature
                    </Button>,
                    !canCreateFeature
                  )
                }
              >
                {features.length === 0 ? (
                  <Empty description="Nenhuma feature cadastrada ainda." />
                ) : (
                  <Table
                    rowKey="id"
                    dataSource={features}
                    columns={featureColumns}
                    pagination={{ pageSize: 10 }}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'entitlements',
            label: 'Entitlements',
            children: (
              <Card
                extra={
                  <Space>
                    <Select
                      placeholder="Selecione um plano"
                      style={{ width: 320 }}
                      value={selectedPlanId}
                      onChange={(value) => setSelectedPlanId(value)}
                      options={plans.map((plan) => ({
                        label: `${plan.code} - ${plan.name}`,
                        value: plan.id,
                      }))}
                    />
                    {withPermissionTooltip(
                      <Button
                        type="primary"
                        disabled={!canCreateEntitlement}
                        onClick={() => {
                          if (!selectedPlanId) {
                            message.warning('Selecione um plano para vincular entitlement.');
                            return;
                          }
                          entitlementForm.resetFields();
                          setCreateEntitlementFeatureType(null);
                          setCreateEntitlementOpen(true);
                        }}
                      >
                        Vincular entitlement
                      </Button>,
                      !canCreateEntitlement
                    )}
                  </Space>
                }
              >
                {!selectedPlan ? (
                  <Empty description="Selecione um plano para visualizar seus entitlements." />
                ) : entitlementsLoading ? (
                  <div style={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin tip="Carregando entitlements..." />
                  </div>
                ) : entitlements.length === 0 ? (
                  <Empty description="Este plano ainda nao possui entitlements." />
                ) : (
                  <Table
                    rowKey="id"
                    dataSource={entitlements}
                    columns={entitlementColumns}
                    pagination={{ pageSize: 10 }}
                  />
                )}
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title="Criar plano"
        open={createPlanOpen}
        okText="Criar"
        cancelText="Cancelar"
        confirmLoading={submitting}
        onOk={handleCreatePlan}
        okButtonProps={{ disabled: !canCreatePlan }}
        onCancel={() => {
          setCreatePlanOpen(false);
          planForm.resetFields();
        }}
      >
        <Form
          layout="vertical"
          form={planForm}
          initialValues={{
            status: 'active',
            billing_period_months: 1,
            base_price_brl: '0,00',
            currency_code: 'BRL',
            trial_days_default: 0,
          }}
        >
          <Form.Item label="Code" name="code" rules={[{ required: true, message: 'Informe o code do plano.' }]}>
            <Input placeholder="PRO_MENSAL" />
          </Form.Item>
          <Form.Item label="Nome" name="name" rules={[{ required: true, message: 'Informe o nome do plano.' }]}>
            <Input placeholder="Pro Mensal" />
          </Form.Item>
          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select options={PLAN_STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item
            label="Periodicidade"
            name="billing_period_months"
            rules={[{ required: true, message: 'Informe billing_period_months.' }]}
          >
            <Select options={BILLING_PERIOD_OPTIONS} />
          </Form.Item>
          <Form.Item
            label="Preco mensal/base (R$)"
            name="base_price_brl"
            rules={[
              { required: true, message: 'Informe o preco em R$.' },
              {
                validator: (_, value) => {
                  if (parseBrlToCents(value) === null) {
                    return Promise.reject(new Error('Informe um valor valido. Ex.: 199,90'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Ex.: 199,90" />
          </Form.Item>
          <Form.Item
            label="Currency"
            name="currency_code"
            rules={[{ required: true, message: 'Informe currency_code.' }]}
          >
            <Input maxLength={3} />
          </Form.Item>
          <Form.Item
            label="Trial default (dias)"
            name="trial_days_default"
            rules={[{ required: true, message: 'Informe trial_days_default.' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="Plano publico (aparece no signup)"
            name="is_public"
            valuePropName="checked"
          >
            <Switch checkedChildren="SIM" unCheckedChildren="NAO" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Criar feature"
        open={createFeatureOpen}
        okText="Criar"
        cancelText="Cancelar"
        confirmLoading={submitting}
        onOk={handleCreateFeature}
        okButtonProps={{ disabled: !canCreateFeature }}
        onCancel={() => {
          setCreateFeatureOpen(false);
          featureForm.resetFields();
        }}
      >
        <Form
          layout="vertical"
          form={featureForm}
          initialValues={{
            status: 'active',
            feature_type: 'boolean',
          }}
        >
          <Form.Item label="Code" name="code" rules={[{ required: true, message: 'Informe o code da feature.' }]}>
            <Input placeholder="FEATURE_FLAG_X" />
          </Form.Item>
          <Form.Item label="Nome" name="name" rules={[{ required: true, message: 'Informe o nome da feature.' }]}>
            <Input placeholder="Feature X" />
          </Form.Item>
          <Form.Item label="Feature type" name="feature_type" rules={[{ required: true }]}>
            <Select options={FEATURE_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item label="Status" name="status" rules={[{ required: true }]}>
            <Select options={PLAN_STATUS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Alterar status do plano"
        open={statusPlanModalState.open}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={submitting}
        onOk={handlePatchPlanStatus}
        okButtonProps={{ disabled: !canWritePlanStatus }}
        onCancel={() => {
          setStatusPlanModalState({ open: false, plan: null });
          planStatusForm.resetFields();
        }}
      >
        <Text>
          Plano: <strong>{statusPlanModalState.plan?.code || '-'}</strong>
        </Text>
        <Form layout="vertical" form={planStatusForm} style={{ marginTop: 12 }}>
          <Form.Item label="Novo status" name="status" rules={[{ required: true }]}> 
            <Select options={PLAN_STATUS_PATCH_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Alterar status da feature"
        open={statusFeatureModalState.open}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={submitting}
        onOk={handlePatchFeatureStatus}
        okButtonProps={{ disabled: !canWriteFeatureStatus }}
        onCancel={() => {
          setStatusFeatureModalState({ open: false, feature: null });
          featureStatusForm.resetFields();
        }}
      >
        <Text>
          Feature: <strong>{statusFeatureModalState.feature?.code || '-'}</strong>
        </Text>
        <Form layout="vertical" form={featureStatusForm} style={{ marginTop: 12 }}>
          <Form.Item label="Novo status" name="status" rules={[{ required: true }]}> 
            <Select options={FEATURE_STATUS_PATCH_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Vincular entitlement"
        open={createEntitlementOpen}
        okText="Vincular"
        cancelText="Cancelar"
        confirmLoading={submitting}
        onOk={handleCreateEntitlement}
        okButtonProps={{ disabled: !canCreateEntitlement }}
        onCancel={() => {
          setCreateEntitlementOpen(false);
          entitlementForm.resetFields();
          setCreateEntitlementFeatureType(null);
        }}
      >
        <Form
          layout="vertical"
          form={entitlementForm}
          onValuesChange={(changedValues, allValues) => {
            if (Object.prototype.hasOwnProperty.call(changedValues, 'feature_id')) {
              const selectedFeature = featuresById.get(allValues.feature_id);
              const featureType = selectedFeature?.feature_type || null;
              setCreateEntitlementFeatureType(featureType);

              if (featureType === 'boolean') {
                entitlementForm.setFieldsValue({ limit_value: null });
              }
              if (featureType === 'limit') {
                entitlementForm.setFieldsValue({ bool_value: null });
              }
            }
          }}
        >
          <Form.Item label="Feature" name="feature_id" rules={[{ required: true, message: 'Selecione a feature.' }]}> 
            <Select
              showSearch
              optionFilterProp="label"
              options={features.map((feature) => ({
                label: `${feature.code} - ${feature.name}`,
                value: feature.id,
              }))}
            />
          </Form.Item>

          {createEntitlementFeatureType === 'boolean' ? (
            <Form.Item label="Bool value" name="bool_value" rules={[{ required: true, message: 'Informe bool_value.' }]}>
              <Select
                options={[
                  { label: 'True', value: true },
                  { label: 'False', value: false },
                ]}
              />
            </Form.Item>
          ) : null}

          {createEntitlementFeatureType === 'limit' ? (
            <Form.Item label="Limit value" name="limit_value" rules={[{ required: true, message: 'Informe limit_value.' }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>

    </div>
  );
};

export default PlatformSaasBillingCatalog;
